// Meta Pixel helper. Safe no-op if REACT_APP_META_PIXEL_ID is empty.
import { CURRENT_PRICE_ARS } from '@/config/pricing';

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
let initialized = false;

// SHA-256 helper para Advanced Matching (Meta requiere el email hasheado, lowercased, trimmed).
async function sha256Hex(input) {
  if (typeof window === 'undefined' || !window.crypto?.subtle) return null;
  const data = new TextEncoder().encode(input);
  const buf = await window.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Activa Advanced Matching enviando el email hasheado a Meta Pixel.
 * Mejora el match rate y el ROAS reportado en Ads Manager.
 * Devuelve también el hash para que el caller lo pueda enviar al backend (CAPI dedup).
 */
export async function setAdvancedMatchingEmail(rawEmail) {
  if (!rawEmail || typeof rawEmail !== 'string') return null;
  const normalized = rawEmail.trim().toLowerCase();
  const hashed = await sha256Hex(normalized);
  if (!hashed) return null;
  if (PIXEL_ID && typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq('set', 'userData', { em: hashed });
    } catch (e) {
      // noop
    }
  }
  return hashed;
}

function loadBaseScript() {
  if (typeof window === 'undefined' || window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
}

export function initMetaPixel() {
  if (!PIXEL_ID || initialized || typeof window === 'undefined') return;
  loadBaseScript();
  if (window.fbq) {
    window.fbq('init', PIXEL_ID);
    initialized = true;
  }
}

export function trackPageView() {
  if (!PIXEL_ID || typeof window === 'undefined' || !window.fbq) return;
  window.fbq('track', 'PageView');
}

export function trackViewContent(payload = {}) {
  if (!PIXEL_ID || typeof window === 'undefined' || !window.fbq) return;
  window.fbq('track', 'ViewContent', {
    currency: 'ARS',
    value: CURRENT_PRICE_ARS,
    content_name: 'EVALPRO ilimitado',
    content_type: 'product',
    content_ids: ['ilimitado'],
    ...payload,
  });
}

export function trackInitiateCheckout(payload = {}, options = {}) {
  if (!PIXEL_ID || typeof window === 'undefined' || !window.fbq) return;

  // Dedup por sesión (misma pestaña). Evita inflar el conteo cuando el usuario
  // recarga /checkout, vuelve atrás y vuelve, o re-mounta el componente. Sólo
  // se dispara la primera vez que hay una intención real de checkout en la sesión.
  // Al cerrar la pestaña, sessionStorage se limpia => nueva sesión => nuevo IC.
  const SESSION_KEY = 'evalpro_ic_fired';
  if (!options.force) {
    try {
      if (window.sessionStorage.getItem(SESSION_KEY) === '1') return;
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch { /* sessionStorage bloqueado (modo privado antiguo) — seguimos y disparamos */ }
  }

  const eventOptions = options.eventID ? { eventID: options.eventID } : undefined;
  window.fbq('track', 'InitiateCheckout', {
    currency: 'ARS',
    value: CURRENT_PRICE_ARS,
    ...payload,
  }, eventOptions);
}

// Purchase: una sola vez por compra (eventID), aunque se recargue la página o
// se vuelva atrás. El mismo eventID va al servidor, que manda el Purchase por
// la API de Conversiones: Meta junta los dos y cuenta una compra.
export function trackPurchase(payload = {}, options = {}) {
  if (!PIXEL_ID || typeof window === 'undefined' || !window.fbq) return;
  const { event_id, ...customData } = payload;
  const fbqOptions = options.eventID ? options : (event_id ? { eventID: event_id } : undefined);
  if (fbqOptions) {
    const clave = `evalpro_purchase_${fbqOptions.eventID}`;
    try {
      if (window.localStorage.getItem(clave)) return;
      window.localStorage.setItem(clave, '1');
    } catch { /* localStorage bloqueado: se manda igual, Meta deduplica por eventID */ }
  }
  window.fbq(
    'track',
    'Purchase',
    {
      currency: 'ARS',
      value: CURRENT_PRICE_ARS,
      ...customData,
    },
    fbqOptions,
  );
}

export function trackLead(payload = {}, options = {}) {
  if (!PIXEL_ID || typeof window === 'undefined' || !window.fbq) return;
  const { event_id, ...customData } = payload;
  const fbqOptions = options.eventID ? options : (event_id ? { eventID: event_id } : undefined);
  window.fbq(
    'track',
    'Lead',
    {
      content_name: 'La entrevista inicial en una hoja',
      content_category: 'lead_magnet',
      ...customData,
    },
    fbqOptions,
  );
}

// Consulta B2B (gimnasios, centros, equipos) desde la landing. Va como evento
// estandar "Contact" y NO como "Lead" a proposito: Lead ya lo dispara el lead
// magnet, y si se mezclan no hay forma de saber si los anuncios B2B traen
// consultas o si solo bajan el PDF gratis.
export function trackContact(payload = {}) {
  if (!PIXEL_ID || typeof window === 'undefined' || !window.fbq) return;
  window.fbq('track', 'Contact', {
    content_name: 'EvalPro instituciones',
    content_category: 'b2b',
    ...payload,
  });
}
